import { Typography } from "@mui/material";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import { SeatBlockReason } from "helpers/perSeatHelpers";

interface CustomSeatBlockedDialogProps {
  /** `null` closes the dialog; otherwise it decides which prompt to render. */
  reason: SeatBlockReason | null;
  onClose: () => void;
  /** CTA for a `limit` block — send the account to the plan picker. */
  onUpgrade?: () => void;
  /**
   * Primary CTA for a `limit` block: buy more seats on the current plan. Left
   * out when the block came from the plan's own seat cap, where upgrading is
   * the only way forward.
   */
  onBuySeats?: () => void;
  /** CTA for a `payment` block — send the account to its payment methods. */
  onUpdateCard?: () => void;
  /** Message from the API, shown in place of the default copy when present. */
  message?: string;
}

const copy: Record<
  SeatBlockReason,
  { title: string; body: string; cta: string }
> = {
  limit: {
    title: "No seats available",
    // Buying seats isn't always on offer — at the plan's own cap the only way
    // forward is a bigger plan, so the body is chosen per case below.
    body: "Every seat you've paid for is in use.",
    cta: "See plans",
  },
  payment: {
    title: "Payment problem",
    body: "We couldn't charge your card for the extra seats. Update your payment method and try again — nothing was changed.",
    cta: "Update payment method",
  },
};

/**
 * Shared prompt for the two outcomes the API refuses on purpose: `406` when
 * there is no free seat (or the plan's seat cap is reached) and `402` when the
 * card is declined while buying seats. Neither is a generic error — each has
 * its own way out — so both are surfaced through this dialog from every screen
 * that fills a seat or changes the seat count.
 */
const CustomSeatBlockedDialog = ({
  reason,
  onClose,
  onUpgrade,
  onBuySeats,
  onUpdateCard,
  message,
}: CustomSeatBlockedDialogProps) => {
  const content = reason ? copy[reason] : null;
  const showBuySeats = reason === "limit" && Boolean(onBuySeats);

  const handleConfirm = () => {
    onClose();
    if (reason === "limit") onUpgrade?.();
    if (reason === "payment") onUpdateCard?.();
  };

  /*
    Buying seats keeps the customer on their plan, so once it's on offer it's
    the only forward action — the plan-picker CTA drops out rather than
    crowding a third button in.
  */
  const body =
    message ||
    (reason === "limit"
      ? `${content?.body} ${
          showBuySeats
            ? "Buy more seats to add people."
            : "This plan allows no more seats — upgrade to add people."
        }`
      : content?.body);

  return (
    <AppModal
      open={Boolean(reason)}
      onClose={onClose}
      title={content?.title}
      // Informational rather than destructive — nothing was changed, and the
      // customer is being offered a way forward.
      tone="neutral"
      size="sm"
      primaryLabel={showBuySeats ? "Buy seats" : content?.cta}
      onPrimary={
        showBuySeats
          ? () => {
              onClose();
              onBuySeats?.();
            }
          : handleConfirm
      }
      cancelLabel="Not now"
      onCancel={onClose}
    >
      <Typography sx={bodyTextSx}>{body}</Typography>
    </AppModal>
  );
};

export default CustomSeatBlockedDialog;
